import { createParser, parseAsInteger, parseAsString, useQueryStates } from 'nuqs'
import { useCallback, useMemo } from 'react'
import { ALL_QUERY_PARAM_VALUE, type UIDocumentStatusFilter } from '~/entities/document'
import { DEFAULT_PAGE_SIZE, TABLE_COLUMNS } from '../config'
import type { DocumentFilters } from './types'
import { useFiltersStoreSelectors } from './use-filters-store'
import { usePaginationSelectors } from './use-pagination-store'
import { usePinnedFiltersSelectors } from './use-pinned-filters-store'

const emptyFilters: DocumentFilters = {
  pageSize: DEFAULT_PAGE_SIZE,
  assetId: '',
  status: [],
  phase: [],
  reviewer: '',
  source: '',
  dateFrom: '',
  dateTo: '',
  sortBy: TABLE_COLUMNS.DATE_RECEIVED,
  sortOrder: 'asc',
  payor: '',
  visibility: '',
  classification: '',
  flagged: 'not_flagged',
}

const initialFilters: DocumentFilters = {
  pageSize: DEFAULT_PAGE_SIZE,
  assetId: '',
  status: ['awaiting'],
  phase: [],
  reviewer: '',
  source: '',
  dateFrom: '',
  dateTo: '',
  sortBy: TABLE_COLUMNS.DATE_RECEIVED,
  sortOrder: 'asc',
  payor: '',
  visibility: '',
  classification: '',
  flagged: 'not_flagged',
}

const statusParser = createParser({
  parse: (query: string | null) => {
    if (!query) return null
    return query === ALL_QUERY_PARAM_VALUE ? [] : (query.split(',') as UIDocumentStatusFilter[])
  },
  serialize: (value: UIDocumentStatusFilter[]) => {
    return value.length === 0 ? ALL_QUERY_PARAM_VALUE : value.join(',')
  },
})

const stringArrayParser = createParser({
  parse: (query: string | null) => {
    if (!query) return null
    return query === ALL_QUERY_PARAM_VALUE ? [] : (query.split(',') as string[])
  },
  serialize: (value: string[]) => {
    return value.length === 0 ? ALL_QUERY_PARAM_VALUE : value.join(',')
  },
})

export function useFilters() {
  const [filters, setFilters] = useQueryStates({
    assetId: parseAsString.withDefault(initialFilters.assetId),
    status: statusParser.withDefault(initialFilters.status),
    phase: stringArrayParser.withDefault(initialFilters.phase),
    reviewer: parseAsString.withDefault(initialFilters.reviewer),
    pageSize: parseAsInteger.withDefault(initialFilters.pageSize),
    source: parseAsString.withDefault(initialFilters.source),
    dateFrom: parseAsString.withDefault(initialFilters.dateFrom),
    dateTo: parseAsString.withDefault(initialFilters.dateTo),
    sortBy: parseAsString.withDefault(initialFilters.sortBy),
    sortOrder: parseAsString.withDefault(initialFilters.sortOrder),
    payor: parseAsString.withDefault(initialFilters.payor),
    visibility: parseAsString.withDefault(initialFilters.visibility),
    classification: parseAsString.withDefault(initialFilters.classification),
    flagged: parseAsString.withDefault(initialFilters.flagged),
  })
  const { resetPagination } = usePaginationSelectors({ pageSize: filters.pageSize })
  const { dateReceivedFilterType, setDateReceivedFilterType } = useFiltersStoreSelectors()

  const updateFilters = useCallback(
    (newFilters: Partial<DocumentFilters>) => {
      setFilters((currentFilters) => ({
        ...currentFilters,
        ...newFilters,
      }))
      resetPagination()
    },
    [resetPagination, setFilters],
  )

  const isDirty = useMemo(() => {
    return (
      filters.status.length > 0 ||
      filters.phase.length > 0 ||
      filters.reviewer !== '' ||
      filters.assetId !== '' ||
      filters.source !== '' ||
      filters.dateFrom !== '' ||
      filters.dateTo !== '' ||
      filters.payor !== '' ||
      filters.visibility !== '' ||
      filters.classification !== '' ||
      filters.flagged !== 'not_flagged'
    )
  }, [
    filters.status,
    filters.phase,
    filters.reviewer,
    filters.assetId,
    filters.source,
    filters.dateFrom,
    filters.dateTo,
    filters.payor,
    filters.visibility,
    filters.classification,
    filters.flagged,
  ])

  const resetFilters = useCallback(() => {
    updateFilters(emptyFilters)
    setDateReceivedFilterType(null)
  }, [updateFilters, setDateReceivedFilterType])

  const resetToInitialFilters = useCallback(() => {
    setFilters(initialFilters)
    resetPagination()
    setDateReceivedFilterType(null)
  }, [setFilters, resetPagination, setDateReceivedFilterType])

  return {
    filters,
    setFilters: updateFilters,
    resetFilters,
    resetToInitialFilters,
    isDirty,
    dateReceivedFilterType,
    setDateReceivedFilterType,
  }
}

export const usePinnedFilters = () => {
  const { pinnedFilters, setPinnedFilters } = usePinnedFiltersSelectors()
  return { pinnedFilters, setPinnedFilters }
}
